"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = require("react");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _reactIntl = require("react-intl");
var _reactQuery = require("react-query");
var _core = require("@folio/stripes/core");
var _smartComponents = require("@folio/stripes/smart-components");
var _components = require("@folio/stripes/components");
var _utils = require("../utils");
var _hooks = require("../hooks");
var _TableBody = _interopRequireDefault(require("./TableBody"));
var _SearchKeyControl = _interopRequireDefault(require("../SearchKeyControl"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const SASQLookupComponent = /*#__PURE__*/(0, _react.forwardRef)((props, ref) => {
  const {
    children,
    fetchParameters = {},
    FilterComponent = () => null,
    FilterPaneHeaderComponent = () => null,
    filterPaneProps = {},
    id,
    intlKey: passedIntlKey,
    intlNS: passedIntlNS,
    labelOverrides = {},
    lookupQueryNamespaceGenerator = _ref => {
      let {
        currentPage,
        namespace,
        id: passedId,
        query
      } = _ref;
      const queryNamespace = [namespace, 'SASQ'];
      if (passedId) {
        queryNamespace.push(passedId);
      }
      queryNamespace.push('viewAll');
      queryNamespace.push(query);
      queryNamespace.push(currentPage);
      return queryNamespace;
    },
    lookupQueryPromise = _ref2 => {
      let {
        endpoint,
        ky,
        queryParams
      } = _ref2;
      return ky.get(`${endpoint}${queryParams}`).json();
    },
    lookupResponseTransform = response => response,
    // Function to transform the response from the query
    mainPaneProps = {},
    mclProps = {},
    noResultsProps = {},
    noSearchField,
    persistedPanesetProps = {},
    queryParameterGenerator = _utils.generateKiwtQuery,
    // Expects a function which accepts SASQ_MAP and nsValues
    RenderBody,
    rowNavigation = true,
    // Default navigation onRowClick
    sasqProps,
    searchableIndexes = [],
    searchFieldAriaLabel,
    searchFieldProps = {}
  } = props;
  const kintIntl = (0, _hooks.useKintIntl)(passedIntlKey, passedIntlNS);
  const [count, setCount] = (0, _react.useState)(0);

  // Grab things we might need from sasqProps if passed
  const {
    query: sasqPropsQuery,
    // If these are overriden we need to make use of them
    queryGetter: sasqPropsQueryGetter,
    querySetter: sasqPropsQuerySetter,
    syncToLocation = true
  } = sasqProps ?? {};

  // We manage our internal state of pagination (URL vs MCL page state) using usePrevNextPagination.
  // Below the queryParameterGenerator can then choose to use the outputs (namely totalRecords and page) as it sees fit.
  const {
    currentPage,
    paginationMCLProps,
    paginationSASQProps
  } = (0, _hooks.usePrevNextPagination)({
    count,
    // totalRecord
    pageSize: fetchParameters.SASQ_MAP?.perPage,
    syncToLocation
  });
  const {
    query: kintSASQQuery,
    queryGetter: kintSASQQueryGetter,
    querySetter: kintSASQQuerySetter
  } = (0, _hooks.useKiwtSASQuery)();
  const query = (0, _react.useMemo)(() => sasqPropsQuery ?? kintSASQQuery, [sasqPropsQuery, kintSASQQuery]);
  const queryGetter = (0, _react.useMemo)(() => sasqPropsQueryGetter ?? kintSASQQueryGetter, [sasqPropsQueryGetter, kintSASQQueryGetter]);
  const querySetter = (0, _react.useMemo)(() => sasqPropsQuerySetter ?? kintSASQQuerySetter, [sasqPropsQuerySetter, kintSASQQuerySetter]);
  const {
    0: namespace
  } = (0, _core.useNamespace)();
  const ky = (0, _core.useOkapiKy)();
  const filterPaneVisibileKey = `${namespace}-${id}-filterPaneVisibility`;
  const queryParams = (0, _react.useMemo)(() => queryParameterGenerator(
  // With Typescript this would be a type of SASQ_MAP, and so totalRecords is a valid property in our new shape
  // In generateKiwtQueryParams we can choose to ignore totalRecords, which while being a valid property is not necessary for KIWT queries
  {
    page: currentPage,
    totalRecords: count,
    ...fetchParameters.SASQ_MAP
  }, query ?? {}), [count, currentPage, fetchParameters.SASQ_MAP, query, queryParameterGenerator]);
  const [filterPaneVisible, setFilterPaneVisible] = (0, _hooks.useLocalStorageState)(filterPaneVisibileKey, true);
  const toggleFilterPane = () => setFilterPaneVisible(!filterPaneVisible);
  const {
    data = {},
    ...restOfQueryProps
  } = (0, _reactQuery.useQuery)(lookupQueryNamespaceGenerator({
    currentPage,
    endpoint: fetchParameters.endpoint,
    namespace,
    id,
    query,
    queryParams
  }), () => {
    return lookupQueryPromise({
      ky,
      queryParams,
      endpoint: fetchParameters.endpoint
    });
  }, {
    enabled: (!!query?.filters || !!query?.query) && !!currentPage,
    ...(fetchParameters.queryOptions ?? {}),
    // select is a parameter supported by useQuery to transform the response
    // Could be possible to instead pass this down along with the queryOptions
    // Additionally this is assuming the lookupResponseTransform is a func as opposed to an object
    select: selectData => {
      return lookupResponseTransform(selectData);
    }
  });
  (0, _react.useEffect)(() => {
    if (count !== data?.totalRecords) {
      setCount(data?.totalRecords);
    }
  }, [count, data.totalRecords]);
  (0, _react.useImperativeHandle)(ref, () => ({
    lookupQueryProps: {
      data,
      ...restOfQueryProps
    },
    queryParams
  }));
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_smartComponents.SearchAndSortQuery, {
    initialSearchState: {
      query: ''
    },
    queryGetter: queryGetter,
    querySetter: querySetter,
    syncToLocation: syncToLocation,
    ...sasqProps,
    ...paginationSASQProps,
    children: sasqRenderProps => {
      const {
        activeFilters,
        filterChanged,
        getFilterHandlers,
        getSearchHandlers,
        onSubmitSearch,
        resetAll,
        searchChanged,
        searchValue
      } = sasqRenderProps;
      const searchHandlers = getSearchHandlers();
      const disableReset = !filterChanged && !searchChanged;
      const filterCount = activeFilters.string ? activeFilters.string.split(',').length : 0;
      const Body = RenderBody ?? _TableBody.default;
      const {
        filterPaneFirstMenu,
        filterPaneLastMenu,
        ...restOfFilterPaneProps
      } = filterPaneProps;
      const {
        mainPaneFirstMenu,
        mainPaneLastMenu,
        ...restOfMainPaneProps
      } = mainPaneProps;
      const internalStateProps = {
        activeFilters,
        filterCount,
        filterPaneVisible,
        searchChanged,
        searchValue,
        setFilterPaneVisible,
        toggleFilterPane
      };
      const renderFirstMenu = () => {
        if (mainPaneFirstMenu) {
          return mainPaneFirstMenu(internalStateProps);
        }
        if (!filterPaneVisible) {
          return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.PaneMenu, {
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_smartComponents.ExpandFilterPaneButton, {
              filterCount: filterCount,
              onClick: toggleFilterPane
            })
          });
        }
        return null;
      };
      return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_smartComponents.PersistedPaneset, {
        appId: namespace,
        id: `${id}-paneset`,
        ...persistedPanesetProps,
        children: [filterPaneVisible && /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Pane, {
          defaultWidth: "20%",
          firstMenu: filterPaneFirstMenu ? filterPaneFirstMenu(internalStateProps) : null,
          id: `${id}-filter-pane`,
          lastMenu: filterPaneLastMenu ? filterPaneLastMenu(internalStateProps) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.PaneMenu, {
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_smartComponents.CollapseFilterPaneButton, {
              onClick: toggleFilterPane
            })
          }),
          paneTitle: /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactIntl.FormattedMessage, {
            id: "stripes-smart-components.searchAndFilter"
          }),
          ...restOfFilterPaneProps,
          children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("form", {
            onSubmit: onSubmitSearch,
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(FilterPaneHeaderComponent, {}), !noSearchField && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
              children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_components.SearchField, {
                ariaLabel: searchFieldAriaLabel,
                autoFocus: true,
                id: `sasq-search-field-${id}`,
                name: "query",
                onChange: e => {
                  if (e.target?.value) {
                    searchHandlers.query(e); // SASQ needs the whole event here
                  } else {
                    searchHandlers.reset();
                  }
                },
                onClear: searchHandlers.reset,
                value: searchValue.query,
                ...searchFieldProps
              }), searchableIndexes?.length > 0 && /*#__PURE__*/(0, _jsxRuntime.jsx)(_SearchKeyControl.default, {
                options: searchableIndexes
              }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
                buttonStyle: "primary",
                disabled: !searchValue.query,
                fullWidth: true,
                type: "submit",
                children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactIntl.FormattedMessage, {
                  id: "stripes-smart-components.search"
                })
              }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
                buttonStyle: "none",
                disabled: disableReset,
                id: "clickable-reset-all",
                onClick: resetAll,
                children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Icon, {
                  icon: "times-circle-solid",
                  children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactIntl.FormattedMessage, {
                    id: "stripes-smart-components.resetAll"
                  })
                })
              })]
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)(FilterComponent, {
              activeFilters: activeFilters.state,
              filterChanged: filterChanged,
              filterHandlers: getFilterHandlers(),
              resetAll: resetAll,
              searchChanged: searchChanged,
              searchHandlers: getSearchHandlers(),
              searchValue: searchValue
            })]
          })
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Pane, {
          defaultWidth: "fill",
          firstMenu: renderFirstMenu(),
          id: `${id}-main-pane`,
          lastMenu: mainPaneLastMenu ? mainPaneLastMenu(internalStateProps) : null,
          noOverflow: true,
          padContent: false,
          paneSub: kintIntl.formatKintMessage({
            id: 'found#Values',
            overrideValue: labelOverrides?.foundValues
          },
          // DEPRECATED data.total should NOT be the first call here, but staying for backwards compatibility. Elsewhere we use data.totalRecords
          {
            total: data?.total ?? data?.totalRecords ?? 0
          }),
          ...restOfMainPaneProps,
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(Body, {
            data: data,
            filterPaneVisible: filterPaneVisible,
            intlKey: passedIntlKey,
            intlNS: passedIntlNS,
            labelOverrides: labelOverrides,
            noResultsProps: noResultsProps,
            query: query,
            rowNavigation: rowNavigation,
            toggleFilterPane: toggleFilterPane,
            ...restOfQueryProps,
            ...sasqRenderProps,
            ...sasqProps,
            ...props,
            mclProps: {
              ...paginationMCLProps,
              ...mclProps
            }
          })
        }), children]
      });
    }
  });
});
SASQLookupComponent.propTypes = {
  children: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.node]),
  fetchParameters: _propTypes.default.object,
  filterPaneProps: _propTypes.default.object,
  FilterComponent: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.node]),
  FilterPaneHeaderComponent: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.node]),
  queryParameterGenerator: _propTypes.default.func,
  id: _propTypes.default.string.isRequired,
  intlKey: _propTypes.default.string,
  intlNS: _propTypes.default.string,
  labelOverrides: _propTypes.default.object,
  lookupQueryNamespaceGenerator: _propTypes.default.func,
  lookupQueryPromise: _propTypes.default.func,
  lookupResponseTransform: _propTypes.default.func,
  mainPaneProps: _propTypes.default.object,
  mclProps: _propTypes.default.object,
  noResultsProps: _propTypes.default.object,
  noSearchField: _propTypes.default.bool,
  persistedPanesetProps: _propTypes.default.object,
  RenderBody: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.node]),
  rowNavigation: _propTypes.default.bool,
  sasqProps: _propTypes.default.object,
  searchableIndexes: _propTypes.default.arrayOf(_propTypes.default.shape({
    key: _propTypes.default.string.isRequired,
    label: _propTypes.default.string
  })),
  searchFieldAriaLabel: _propTypes.default.string,
  searchFieldProps: _propTypes.default.object
};
var _default = exports.default = SASQLookupComponent;